/**
 * AnalyticsApiClient
 * 
 * Handles analytics, statistics, and reporting
 */

import { BaseApiClient } from './BaseApiClient';

export class AnalyticsApiClient extends BaseApiClient {
  /**
   * Get enhanced statistics
   */
  async getStats(options?: {
    users?: string[];
    userGroup?: string;
    currencies?: string[];
    dateRange?: {
      start: Date;
      end: Date;
    };
    timePeriod?: 'today' | 'yesterday' | 'this-week' | 'last-week' | 'this-month' | 'last-month' | 'this-year' | 'last-year';
    includeBalances?: boolean;
    includeBreakdowns?: boolean;
    includeGroupMembers?: boolean;
  }): Promise<{
    period: string;
    dateRange: {
      start: Date;
      end: Date;
    };
    filters: {
      users: string[];
      currencies: string[] | 'all';
      includeGroupMembers: boolean;
    };
    overview: {
      totalTransactions: number;
      activeCurrencies: string[];
      totalUsers: number;
      totalVolume: Record<string, number>;
    };
    statistics: {
      created: { count: number; value: Record<string, number>; transactions: any[] };
      redeemed: { count: number; value: Record<string, number>; transactions: any[] };
      expired: { count: number; value: Record<string, number>; transactions: any[] };
      pending: { count: number; value: Record<string, number>; transactions: any[] };
    };
    byCurrency: Record<string, {
      transactions: number;
      volume: number;
      created: number;
      redeemed: number;
    }>;
    balances?: Record<string, number>;
    tokenTypes?: {
      cash: { count: number; value: Record<string, number> };
      invoice: { count: number; value: Record<string, number> };
      referral: { count: number; value: Record<string, number> };
      balance_up: { count: number; value: Record<string, number> };
      balance_down: { count: number; value: Record<string, number> };
    };
    detailedTransactions?: {
      created: any[];
      redeemed: any[];
      expired: any[];
      pending: any[];
    };
  }> {
    try {
      console.log('🔍 Getting enhanced stats with options:', options);
      
      if (options && Object.keys(options).length > 0) {
        // Use POST for complex filtering
        const response = await this.post<{
          stats: any;
        }>('/get-stats', options);
        
        console.log('✅ Stats retrieved successfully via POST:', response);
        return response.stats;
      } else {
        // Use simple GET for basic stats
        const response = await this.get<{
          stats: any;
        }>('/get-stats');
        
        console.log('✅ Stats retrieved successfully via GET:', response);
        return response.stats;
      }
    } catch (error) {
      console.error('❌ Failed to get stats:', error);
      throw error;
    }
  }

  /**
   * Get user analytics dashboard
   */
  async getUserAnalytics(period: '7d' | '30d' | '90d' | '1y' = '30d'): Promise<{
    period: string;
    totalTransactions: number;
    totalVolume: Record<string, number>;
    averageTransactionSize: Record<string, number>;
    transactionTrends: Array<{
      date: string;
      transactions: number;
      volume: Record<string, number>;
    }>;
    topCurrencies: Array<{
      currency: string;
      transactions: number;
      volume: number;
      percentage: number;
    }>;
    paymentMethodUsage: Array<{
      method: string;
      count: number;
      percentage: number;
    }>;
  }> {
    return this.get<{
      period: string;
      totalTransactions: number;
      totalVolume: Record<string, number>;
      averageTransactionSize: Record<string, number>;
      transactionTrends: Array<{
        date: string;
        transactions: number;
        volume: Record<string, number>;
      }>;
      topCurrencies: Array<{
        currency: string;
        transactions: number;
        volume: number;
        percentage: number;
      }>;
      paymentMethodUsage: Array<{
        method: string;
        count: number;
        percentage: number;
      }>;
    }>(`/analytics/user?period=${period}`);
  }

  /**
   * Get platform-wide analytics (admin only)
   */
  async getPlatformAnalytics(period: '7d' | '30d' | '90d' | '1y' = '30d'): Promise<{
    period: string;
    totalUsers: number;
    activeUsers: number;
    newUsers: number;
    totalTransactions: number;
    totalVolume: Record<string, number>;
    averageTransactionValue: Record<string, number>;
    userGrowth: Array<{
      date: string;
      newUsers: number;
      totalUsers: number;
    }>;
    transactionVolume: Array<{
      date: string;
      transactions: number;
      volume: Record<string, number>;
    }>;
    topCurrencies: Array<{
      currency: string;
      transactions: number;
      volume: number;
      users: number;
    }>;
    userEngagement: {
      dailyActiveUsers: number;
      weeklyActiveUsers: number;
      monthlyActiveUsers: number;
      averageSessionDuration: number;
    };
  }> {
    return this.get<{
      period: string;
      totalUsers: number;
      activeUsers: number;
      newUsers: number;
      totalTransactions: number;
      totalVolume: Record<string, number>;
      averageTransactionValue: Record<string, number>;
      userGrowth: Array<{
        date: string;
        newUsers: number;
        totalUsers: number;
      }>;
      transactionVolume: Array<{
        date: string;
        transactions: number;
        volume: Record<string, number>;
      }>;
      topCurrencies: Array<{
        currency: string;
        transactions: number;
        volume: number;
        users: number;
      }>;
      userEngagement: {
        dailyActiveUsers: number;
        weeklyActiveUsers: number;
        monthlyActiveUsers: number;
        averageSessionDuration: number;
      };
    }>(`/analytics/platform?period=${period}`);
  }

  /**
   * Get token analytics
   */
  async getTokenAnalytics(period: '7d' | '30d' | '90d' | '1y' = '30d'): Promise<{
    period: string;
    totalTokens: number;
    activeTokens: number;
    redeemedTokens: number;
    expiredTokens: number;
    totalTokenValue: Record<string, number>;
    averageTokenValue: Record<string, number>;
    tokenTypes: {
      cash: { count: number; value: Record<string, number> };
      invoice: { count: number; value: Record<string, number> };
      referral: { count: number; value: Record<string, number> };
    };
    redemptionRate: number;
    averageTimeToRedemption: number;
    tokenTrends: Array<{
      date: string;
      created: number;
      redeemed: number;
      expired: number;
    }>;
  }> {
    return this.get<{
      period: string;
      totalTokens: number;
      activeTokens: number;
      redeemedTokens: number;
      expiredTokens: number;
      totalTokenValue: Record<string, number>;
      averageTokenValue: Record<string, number>;
      tokenTypes: {
        cash: { count: number; value: Record<string, number> };
        invoice: { count: number; value: Record<string, number> };
        referral: { count: number; value: Record<string, number> };
      };
      redemptionRate: number;
      averageTimeToRedemption: number;
      tokenTrends: Array<{
        date: string;
        created: number;
        redeemed: number;
        expired: number;
      }>;
    }>(`/analytics/tokens?period=${period}`);
  }

  /**
   * Get payment method analytics
   */
  async getPaymentMethodAnalytics(period: '7d' | '30d' | '90d' | '1y' = '30d'): Promise<{
    period: string;
    totalPayments: number;
    successfulPayments: number;
    failedPayments: number;
    successRate: number;
    totalVolume: Record<string, number>;
    averagePaymentValue: Record<string, number>;
    paymentMethods: Array<{
      method: string;
      count: number;
      volume: Record<string, number>;
      successRate: number;
      averageProcessingTime: number;
    }>;
    paymentTrends: Array<{
      date: string;
      successful: number;
      failed: number;
      volume: Record<string, number>;
    }>;
  }> {
    return this.get<{
      period: string;
      totalPayments: number;
      successfulPayments: number;
      failedPayments: number;
      successRate: number;
      totalVolume: Record<string, number>;
      averagePaymentValue: Record<string, number>;
      paymentMethods: Array<{
        method: string;
        count: number;
        volume: Record<string, number>;
        successRate: number;
        averageProcessingTime: number;
      }>;
      paymentTrends: Array<{
        date: string;
        successful: number;
        failed: number;
        volume: Record<string, number>;
      }>;
    }>(`/analytics/payments?period=${period}`);
  }

  /**
   * Generate custom report
   */
  async generateReport(reportConfig: {
    name: string;
    type: 'transactions' | 'users' | 'tokens' | 'payments' | 'custom';
    dateRange: {
      start: string;
      end: string;
    };
    metrics: string[];
    filters?: Record<string, any>;
    groupBy?: string;
    format?: 'json' | 'csv' | 'pdf';
  }): Promise<{
    reportId: string;
    status: 'generating' | 'completed' | 'failed';
    downloadUrl?: string;
    estimatedCompletion?: string;
  }> {
    return this.post<{
      reportId: string;
      status: 'generating' | 'completed' | 'failed';
      downloadUrl?: string;
      estimatedCompletion?: string;
    }>('/analytics/reports/generate', reportConfig);
  }

  /**
   * Get report status
   */
  async getReportStatus(reportId: string): Promise<{
    reportId: string;
    status: 'generating' | 'completed' | 'failed';
    progress: number;
    downloadUrl?: string;
    error?: string;
  }> {
    return this.get<{
      reportId: string;
      status: 'generating' | 'completed' | 'failed';
      progress: number;
      downloadUrl?: string;
      error?: string;
    }>(`/analytics/reports/${reportId}/status`);
  }

  /**
   * Get user's reports
   */
  async getUserReports(): Promise<{
    reports: Array<{
      id: string;
      name: string;
      type: string;
      status: string;
      createdAt: string;
      completedAt?: string;
      downloadUrl?: string;
    }>;
  }> {
    return this.get<{
      reports: Array<{
        id: string;
        name: string;
        type: string;
        status: string;
        createdAt: string;
        completedAt?: string;
        downloadUrl?: string;
      }>;
    }>('/analytics/reports/user');
  }
} 